import { lazy, Suspense } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import HeroSection from "@/components/home/HeroSection";
import USAHeroSection from "@/components/home/USAHeroSection";
import { homepageFaqs } from "@/components/home/homepageFaqs";
import { RegionalSEO } from "@/components/seo/RegionalSEO";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { useI18n } from "@/lib/i18n";

// Below-the-fold sections, lazy-loaded to shrink initial bundle
const ServicePillars = lazy(() => import("@/components/home/ServicePillars"));
const HowItWorks = lazy(() => import("@/components/home/HowItWorks"));
const TurnkeyPartnerSection = lazy(() => import("@/components/home/TurnkeyPartnerSection"));
const DivisionsShowcase = lazy(() => import("@/components/home/DivisionsShowcase"));
const WhyMTLI = lazy(() => import("@/components/home/WhyMTLI"));
const IndustriesSection = lazy(() => import("@/components/home/IndustriesSection"));
const USAIndustriesSection = lazy(() => import("@/components/home/USAIndustriesSection"));
const FacilityPlanningSection = lazy(() => import("@/components/home/FacilityPlanningSection"));
const CTASection = lazy(() => import("@/components/home/CTASection"));
const EbookSection = lazy(() => import("@/components/home/EbookSection"));
const HomepageFAQ = lazy(() => import("@/components/home/HomepageFAQ"));
const SocialFeed = lazy(() => import("@/components/home/DeferredSocialFeed"));
const FeaturedCaseStudies = lazy(() => import("@/components/home/FeaturedCaseStudies"));
const InternalLinks = lazy(() => import("@/components/seo/InternalLinks"));


const Index = () => {
  const { region, config, isFrench, isUSA } = useI18n();

  return (
    <div className="min-h-screen flex flex-col">
      <RegionalSEO
        title={isUSA ? "Advanced Warehouse Automation & Facility Solutions | MTLI" : "Warehouse Automation Company"}
        exactTitle={isUSA}
        description={isUSA
          ? "Explore warehouse automation, construction, and turnkey facility solutions from MTLI Group. Build, integrate, and maintain your facility with one partner. Talk to us."
          : "Looking to scale with smarter warehouse automation? Optimize processes, improve inventory control, and increase efficiency. Book a call with MTLI."}
      />

      <SchemaMarkup
        schemas={[
          {
            type: "BreadcrumbList",
            items: [{ name: "Home", url: "/" }],
          },
          ...(isUSA
            ? [{ type: "FAQPage" as const, questions: homepageFaqs }]
            : []),
        ]}
      />
      <Header />
      <main className="flex-grow">
        {isUSA ? (
          <>
            {/* USA Layout, conversion-first: Hero w/ callback form → How it works → Services picker → Why MTLI → Case studies → FAQ → CTA.
                Industries/E-book/Social moved off the landing page to keep the funnel tight. */}
            <USAHeroSection />
            <Suspense fallback={null}>
              <TurnkeyPartnerSection />
              <DivisionsShowcase />
              <USAIndustriesSection />
              <HowItWorks />
              <WhyMTLI />
              <FacilityPlanningSection />
              <FeaturedCaseStudies />
              <HomepageFAQ />
              <CTASection />
            </Suspense>
          </>
        ) : (
          <>
            {/* Canada Layout: Video hero → Service pillars → Why MTLI → E-book → Industries → Social → CTA */}
            <HeroSection />
            <Suspense fallback={null}>
              <ServicePillars />
              <WhyMTLI />
              <EbookSection />
              <IndustriesSection />
              <SocialFeed title={isFrench ? "Suivez notre parcours" : "Follow Our Journey"} />

              <CTASection />
            </Suspense>
          </>
        )}
        <Suspense fallback={null}>
          <InternalLinks currentType="service" showAll />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
